'use client';

import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Star, RotateCcw, Hash, UserCheck, Ticket, Camera, X, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { EntryData } from './GameFlow';
import SalesRepModal from './SalesRepModal';
import { emailSettingsService, applyBuilderTemplate } from '@/lib/services/emailSettingsService';
import { entriesService } from '@/lib/services/entriesService';

interface SuccessPhaseProps {
  entry: EntryData;
  onPlayAgain: () => void;
  gamesPlayed?: string[];
  entryId: string | null;
  manateeTimeMs?: number | null;
  raffleCode?: string | null;
}

export default function SuccessPhase({ entry, onPlayAgain, gamesPlayed, entryId, manateeTimeMs, raffleCode }: SuccessPhaseProps) {
  const [showSalesRep, setShowSalesRep] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Camera state
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [photoTaken, setPhotoTaken] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraLoading, setCameraLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Send confirmation email on mount if enabled
  useEffect(() => {
    const sendEmail = async () => {
      try {
        const settings = await emailSettingsService.getSettings();
        if (!settings || !settings.enabled) return;

        // Build the final HTML from the stored builder HTML (already contains all blocks
        // including any banner/logo blocks placed by the drag-and-drop builder)
        const htmlBody = applyBuilderTemplate(settings.htmlTemplate, {
          playerName: entry.name,
          email: entry.email,
          company: entry.company,
          guess: entry.guess,
          manateeTimeMs: manateeTimeMs ?? null,
          gamesPlayed: gamesPlayed ?? [],
          raffleCode: raffleCode ?? null,
        });

        await emailSettingsService.sendConfirmationEmail({
          to: entry.email,
          fromName: settings.fromName,
          subject: settings.subject,
          htmlBody,
          entryId: entryId ?? undefined,
          siteUrl: process.env.NEXT_PUBLIC_SITE_URL,
        });
      } catch (err: any) {
        // Show non-blocking error hint
        setEmailError(err?.message ?? 'Email could not be sent');
      }
    };

    sendEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, [stream]);

  // Attach stream to video element whenever stream changes
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const openCamera = async () => {
    setCameraError(null);
    setCameraLoading(true);
    setCameraOpen(true);

    // Try environment (rear) camera first on mobile, fall back to user (front)
    const constraints: MediaStreamConstraints[] = [
      { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
      { video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
      { video: true, audio: false },
    ];

    let mediaStream: MediaStream | null = null;
    let lastError: string = '';

    for (const constraint of constraints) {
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraint);
        break;
      } catch (err: any) {
        lastError = err?.name === 'NotAllowedError' ?'Camera permission denied. Please allow camera access in your browser settings and try again.'
          : err?.name === 'NotFoundError' ?'No camera found on this device.'
          : err?.name === 'NotReadableError' ?'Camera is in use by another application. Please close other apps and try again.'
          : `Camera error: ${err?.message ?? 'Unknown error'}`;
        if (err?.name === 'NotAllowedError') break; // No point retrying if permission denied
      }
    }

    setCameraLoading(false);

    if (mediaStream) {
      setStream(mediaStream);
    } else {
      setCameraError(lastError || 'Could not access camera. Please check permissions and try again.');
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    setCameraOpen(false);
    setCameraError(null);
    setCameraLoading(false);
  };

  const takePhoto = async () => {
    if (!videoRef.current || !entryId) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      setUploadingPhoto(true);
      try {
        const url = await entriesService.uploadPlayerPhoto(entryId, blob);
        setPhotoUrl(url);
        setPhotoTaken(true);
        closeCamera();
      } catch (err: any) {
        setCameraError(`Failed to save photo: ${err?.message ?? 'Please try again.'}`);
      } finally {
        setUploadingPhoto(false);
      }
    }, 'image/jpeg', 0.9);
  };

  const handleNotesSaved = () => {
    setNotesSaved(true);
  };

  return (
    <div className="animate-bounce-in">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full shadow-xl shadow-green-200 mb-4">
          <CheckCircle size={44} className="text-white" strokeWidth={2.5} />
        </div>
        <h2 className="text-3xl font-extrabold text-gray-800">You&apos;re In!</h2>
        <p className="text-gray-500 text-sm mt-1">Your entry has been recorded. Good luck! 🍀</p>
      </div>

      {/* Email error — non-blocking, dismissible */}
      {emailError && (
        <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
          <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Confirmation email issue</p>
            <p className="text-xs mt-0.5 opacity-80">{emailError}</p>
          </div>
          <button onClick={() => setEmailError(null)} className="text-amber-400 hover:text-amber-600 flex-shrink-0">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Summary card */}
      <div className="bg-white rounded-3xl shadow-xl border border-green-100 p-6 mb-4">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-4">
          Your Entry Summary
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-500 font-medium">Name</span>
            <span className="text-sm font-semibold text-gray-800">{entry.name}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-500 font-medium">Email</span>
            <span className="text-sm font-semibold text-gray-800 truncate max-w-[200px]">{entry.email}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-500 font-medium">Company</span>
            <span className="text-sm font-semibold text-gray-800">{entry.company}</span>
          </div>
          {entry.guess > 0 && (
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500 font-medium">Your Guess</span>
              <div className="flex items-center gap-1.5 bg-yellow-50 border border-yellow-200 rounded-xl px-3 py-1">
                <Hash size={14} className="text-yellow-600" />
                <span className="text-base font-extrabold text-yellow-700 font-tabular">{entry.guess.toLocaleString()}</span>
              </div>
            </div>
          )}
          {gamesPlayed && gamesPlayed.length > 0 && (
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <span className="text-sm text-gray-500 font-medium">Games Played</span>
              <div className="flex gap-1.5 flex-wrap justify-end">
                {gamesPlayed.map((g) => (
                  <span key={g} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${g === 'candy-count' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                    {g === 'candy-count' ? '🦭 Count Manatees' : '🔍 Find Manatee'}
                  </span>
                ))}
              </div>
            </div>
          )}
          {raffleCode && (
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-500 font-medium flex items-center gap-1.5">
                <Ticket size={14} className="text-purple-500" />
                Raffle Code
              </span>
              <div className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 rounded-xl px-3 py-1">
                <span className="text-base font-extrabold text-purple-700 tracking-widest font-tabular">{raffleCode}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Prize info */}
      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl p-4 mb-4 flex items-center gap-3 shadow-md shadow-yellow-200">
        <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center flex-shrink-0">
          <Star size={22} className="text-yellow-900" fill="currentColor" />
        </div>
        <div>
          <p className="font-bold text-yellow-900 text-sm">Winners will be announced on our LinkedIn channel</p>
          <p className="text-yellow-800/70 text-xs mt-0.5">Keep your raffle code — winners will be announced on our LinkedIn channel after the event!</p>
        </div>
      </div>

      {/* Smile for the Camera button */}
      {entryId && !photoTaken && (
        <button
          onClick={openCamera}
          className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 active:scale-95 py-3.5 rounded-2xl shadow-md shadow-pink-200 transition-all duration-150 mb-3"
        >
          <Camera size={18} />
          😊 Smile for the Camera!
        </button>
      )}

      {/* Photo taken confirmation */}
      {photoTaken && photoUrl && (
        <div className="mb-3 rounded-2xl overflow-hidden border-2 border-green-300 shadow-md">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={photoUrl} alt="Player photo" className="w-full object-cover max-h-48" />
          <div className="bg-green-50 px-4 py-2 flex items-center gap-2">
            <CheckCircle size={14} className="text-green-600" />
            <span className="text-xs font-semibold text-green-700">Photo saved to your entry!</span>
          </div>
        </div>
      )}

      {/* Sales Rep section */}
      {entryId && !notesSaved && (
        <button
          onClick={() => setShowSalesRep(true)}
          className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700 border border-indigo-200 hover:border-indigo-300 bg-white hover:bg-indigo-50 py-3 rounded-xl transition-all duration-150 shadow-sm mb-3"
        >
          <UserCheck size={16} />
          Sales Rep — Add Notes
        </button>
      )}

      {notesSaved ? (
        <button onClick={onPlayAgain} className="w-full bg-green-600 hover:bg-green-700 active:scale-95 text-white font-bold text-sm py-4 rounded-2xl shadow-lg shadow-green-200 transition-all duration-150 flex items-center justify-center gap-2 mb-3">
          <RotateCcw size={16} />
          Reset Game — New Player
        </button>
      ) : (
        <button onClick={onPlayAgain} className="w-full bg-white hover:bg-gray-50 active:scale-95 text-green-700 font-bold text-sm py-3.5 rounded-2xl border-2 border-green-200 transition-all duration-150 flex items-center justify-center gap-2 mb-3">
          <RotateCcw size={16} />
          Play Again (New Entry)
        </button>
      )}

      <button onClick={onPlayAgain} className="w-full bg-gray-800 hover:bg-gray-900 active:scale-95 text-white font-bold text-sm py-4 rounded-2xl shadow-lg transition-all duration-150 flex items-center justify-center gap-2">
        <RotateCcw size={16} />
        Start Over
      </button>

      {showSalesRep && entryId && (
        <SalesRepModal entryId={entryId} onClose={() => setShowSalesRep(false)} onSaved={handleNotesSaved} />
      )}

      {/* Camera Modal */}
      {cameraOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Camera size={18} className="text-pink-500" />
                <h3 className="text-base font-bold text-gray-800">Smile for the Camera! 😊</h3>
              </div>
              <button onClick={closeCamera} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-150">
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="relative bg-black min-h-[200px] flex items-center justify-center">
              {cameraError ? (
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-8 text-center">
                  <AlertTriangle size={32} className="text-amber-400" />
                  <p className="text-sm text-gray-300 leading-relaxed">{cameraError}</p>
                </div>
              ) : cameraLoading ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 size={28} className="text-white animate-spin" />
                  <p className="text-xs text-gray-400">Requesting camera access...</p>
                </div>
              ) : stream ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full object-cover max-h-72"
                />
              ) : (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={28} className="text-white animate-spin" />
                </div>
              )}
            </div>

            <div className="p-5 space-y-3">
              {cameraError ? (
                <div className="space-y-2">
                  <button onClick={() => { setCameraError(null); openCamera(); }} className="w-full py-3 rounded-2xl bg-pink-500 hover:bg-pink-600 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
                    <RefreshCw size={15} />
                    Try Again
                  </button>
                  <button onClick={closeCamera} className="w-full py-2.5 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm">
                    Close
                  </button>
                </div>
              ) : (
                <button
                  onClick={takePhoto}
                  disabled={!stream || uploadingPhoto || cameraLoading}
                  className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md shadow-pink-200 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
                >
                  {uploadingPhoto ? (
                    <><Loader2 size={16} className="animate-spin" /> Saving Photo...</>
                  ) : (
                    <><Camera size={16} /> Take Photo</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}